import React, { useEffect, useState, useRef } from 'react';

import './LiveTranscript.scss';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { usePlayer } from '../../contexts/PlayerContext';

type LiveTranscriptProps = {
	sideWindowOpen?: boolean;
	playerHover: boolean;
};

const VIDEO_ASPECT_RATIO = 9 / 16; // 16:9 video

const LiveTranscript = ({ sideWindowOpen, playerHover }: LiveTranscriptProps) => {
	const { transcriptions, currentMediaId, isUserSeekedPlayer } = useConfiguration();
	const { currentPosition } = usePlayer();

	const [lines, setLines] = useState<string[]>(['', '']);
	const [isShifting, setIsShifting] = useState(false);
	const [maxLineWidth, setMaxLineWidth] = useState(800);

	const measureRef = useRef<HTMLSpanElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const currentLineWordsRef = useRef<string[]>([]);
	const pendingWordsRef = useRef<string[]>([]);
	const shownEntriesRef = useRef<Set<string>>(new Set());
	const isInitializedRef = useRef(false);

	// Update max line width and vertical position
	useEffect(() => {
		const updateMaxLineWidth = () => {
			if (containerRef.current) {
				const containerWidth = containerRef.current.offsetWidth * 0.95;
				setMaxLineWidth(containerWidth);
			}
		};

		const updateVerticalPosition = () => {
			if (!containerRef.current) {
				return;
			}

			const playerWindow = document.querySelector('.playerWindow');

			if (!playerWindow) {
				return;
			}

			const screenWidth = playerWindow.clientWidth;
			const screenHeight = playerWindow.clientHeight;

			const isVerticalScreen = screenHeight > screenWidth;

			if (!isVerticalScreen) {
				// Horizontal screen - default positioning
				containerRef.current.style.setProperty('bottom', `${playerHover ? 60 : 20}px`);
				containerRef.current.style.setProperty('transform', 'translateX(-50%)');
				return;
			}

			// Vertical screen - calculate video height based on 16:9
			const videoRenderHeight = screenWidth * VIDEO_ASPECT_RATIO;
			const blackBarHeight = (screenHeight - videoRenderHeight) / 2;
			const bottomPosition = blackBarHeight;

			containerRef.current.style.setProperty('bottom', `${bottomPosition}px`);
			containerRef.current.style.setProperty('transform', 'translateX(-50%)');
		};

		const updateFontSize = () => {
			if (!containerRef.current) {
				return;
			}
			const playerWindow = document.querySelector('.playerWindow');

			if (!playerWindow) {
				return;
			}
			const screenWidth = playerWindow.clientWidth;

			// Use px for stability
			let fontSize = '48px';
			if (screenWidth <= 400) {
				fontSize = '14px';
			} else if (screenWidth <= 576) {
				fontSize = '16px';
			} else if (screenWidth <= 768) {
				fontSize = '18px';
			} else if (screenWidth <= 992) {
				fontSize = '20px';
			} else if (screenWidth <= 1200) {
				fontSize = '32px';
			}

			containerRef.current.style.setProperty('font-size', fontSize);
		};

		const updateStyling = () => {
			updateMaxLineWidth();
			updateVerticalPosition();
			updateFontSize();
		};

		// Explicitly handle browser fullscreen changes.
		// On some platforms (e.g. Windows Chrome) toggling fullscreen may not always
		// trigger a regular resize/orientation event in the same way as macOS,
		// which can leave the subtitle position using stale `playerHover` state.
		const handleFullscreenChange = () => {
			updateStyling();
		};

		// Initial update
		updateStyling();

		// Handle resize and orientation
		window.addEventListener('resize', updateStyling);
		window.addEventListener('orientationchange', updateStyling);
		document.addEventListener('fullscreenchange', handleFullscreenChange);

		return () => {
			window.removeEventListener('resize', updateStyling);
			window.removeEventListener('orientationchange', updateStyling);
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
		};
	}, [currentMediaId, sideWindowOpen, playerHover]);

	// Measure text width
	const measureTextWidth = (text: string): number => {
		if (!measureRef.current) {
			return 0;
		}
		measureRef.current.textContent = text;
		return measureRef.current.offsetWidth;
	};

	// Process words (either pending or new)
	const processWords = (words: string[]) => {
		for (let i = 0; i < words.length; i++) {
			const word = words[i];
			const testLine = [...currentLineWordsRef.current, word].join(' ');

			if (measureTextWidth(testLine) > maxLineWidth && currentLineWordsRef.current.length > 0) {
				pendingWordsRef.current = words.slice(i);
				setIsShifting(true);
				setLines((prev) => [prev[0], currentLineWordsRef.current.join(' ')]);

				setTimeout(() => {
					setLines((prev) => [prev[1], '']);
					currentLineWordsRef.current = [];
					setIsShifting(false);
				}, 300);

				return;
			}

			currentLineWordsRef.current = [...currentLineWordsRef.current, word];
			setLines((prev) => [prev[0], currentLineWordsRef.current.join(' ')]);
		}
	};

	// Process pending words after shift completes
	useEffect(() => {
		if (!isShifting && pendingWordsRef.current.length > 0) {
			const pending = [...pendingWordsRef.current];
			pendingWordsRef.current = [];
			processWords(pending);
		}
	}, [isShifting, maxLineWidth]);

	// Initialize - mark all past entries as shown on first render
	useEffect(() => {
		if (isInitializedRef.current || !transcriptions?.entries?.length) {
			return;
		}

		const currentTimeMs = (currentPosition || 0) * 1000;

		for (const entry of transcriptions.entries) {
			if (entry.startTime < currentTimeMs) {
				const entryKey = `${entry.startTime}-${entry.content}`;
				shownEntriesRef.current.add(entryKey);
			}
		}

		isInitializedRef.current = true;
	}, [transcriptions?.entries, currentPosition]);

	// Process transcriptions when they change
	useEffect(() => {
		if (!isInitializedRef.current || isShifting || !transcriptions?.entries?.length) {
			return;
		}

		const currentTimeMs = (currentPosition || 0) * 1000;
		const validEntries = transcriptions.entries.filter(({ startTime }: any) => currentTimeMs >= startTime);

		if (!validEntries.length) {
			return;
		}

		const newWords: string[] = [];

		for (const entry of validEntries) {
			const entryKey = `${entry.startTime}-${entry.content}`;

			if (shownEntriesRef.current.has(entryKey)) {
				continue;
			}

			shownEntriesRef.current.add(entryKey);

			const content = entry.content?.trim();
			if (content) {
				const words = content.split(/\s+/).filter(Boolean);
				newWords.push(...words);
			}
		}

		if (newWords.length > 0) {
			processWords(newWords);
		}
	}, [transcriptions, currentPosition, isShifting, maxLineWidth]);

	// Reset on seeked
	useEffect(() => {
		if (!isUserSeekedPlayer || !transcriptions?.entries?.length) {
			return;
		}

		const currentTimeMs = (currentPosition || 0) * 1000;

		setLines(['', '']);
		currentLineWordsRef.current = [];
		pendingWordsRef.current = [];
		shownEntriesRef.current.clear();

		for (const entry of transcriptions.entries) {
			if (entry.startTime < currentTimeMs) {
				const entryKey = `${entry.startTime}-${entry.content}`;
				shownEntriesRef.current.add(entryKey);
			}
		}
	}, [isUserSeekedPlayer, currentPosition, transcriptions?.entries]);

	// Reset on media change
	useEffect(() => {
		setLines(['', '']);
		currentLineWordsRef.current = [];
		pendingWordsRef.current = [];
		shownEntriesRef.current.clear();
		isInitializedRef.current = false;
	}, [currentMediaId]);

	return (
		<div ref={containerRef} className={`live-transcript-container ${isShifting ? 'live-transcript-shifting' : ''}`}>
			<span ref={measureRef} className="live-transcript-measure" aria-hidden="true" />
			<div className="live-transcript-lines-wrapper">
				<div className="live-transcript-line live-transcript-first">{lines[0]}</div>
				<div className="live-transcript-line live-transcript-second">{lines[1]}</div>
			</div>
		</div>
	);
};

export default LiveTranscript;
